import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import store from 'src/store';
import type { AppThunk } from 'src/store';
import patchObj from 'src/utils/patchObj';
import { protectedAxios as axios } from 'src/utils/auth';
import { urls } from 'src/utils/api';
import collectObjValues from 'src/utils/collectObjValues';

interface PassportUploadState {
  uploadModalOpened: boolean;
  parsingResult: any[];
}

const initialState: PassportUploadState = {
  uploadModalOpened: false,
  parsingResult: []
};

const slice = createSlice({
  name: 'passportsUpload',
  initialState,
  reducers: {
    setParsingResult(
      state: PassportUploadState,
      action: PayloadAction<any[]>
    ) {
      state.parsingResult = action.payload.map(
        (passport, index) => ({
          ...passport,
          index
        })
      );
    },
    setUploadModalOpened(
      state: PassportUploadState,
      action: PayloadAction<boolean>
    ) {
      state.uploadModalOpened = action.payload;
    },
    patchParsedPassport(
      state: PassportUploadState,
      action: PayloadAction<{ passportIndex: number, field: string, value: any }>
    ) {
      const {
        passportIndex,
        field,
        value
      } = action.payload;

      let targetIndex;

      const targetPassport = state.parsingResult.find(
        (passport, i) => {
          const matches = passport.index === passportIndex;

          if (matches) targetIndex = i;

          return matches;
        }
      );

      if (Number.isInteger(targetIndex) && targetPassport) {
        state.parsingResult[targetIndex] = patchObj(targetPassport, field, value);
      }
    },
    checkPatchedPassport(
      state: PassportUploadState,
      action: PayloadAction<{ passportIndex: number, patchedPassport: any }>
    ) {
      const {
        passportIndex,
        patchedPassport
      } = action.payload;

      const targetPassportIndex = state.parsingResult.findIndex(
        (passport) => passport.index === passportIndex
      );

      if (patchedPassport) {
        state.parsingResult[targetPassportIndex] = patchedPassport;
      } else {
        state.parsingResult[targetPassportIndex].errors = null;
        state.parsingResult[targetPassportIndex].type_error = null;
      }
    },
    removePassportError(
      state: PassportUploadState,
      action: PayloadAction<{ passportIndex: number, field: string }>
    ) {
      const {
        passportIndex,
        field
      } = action.payload;

      let targetIndex;

      const targetPassport = state.parsingResult.find(
        (passport, i) => {
          const matches = passport.index === passportIndex;

          if (matches) targetIndex = i;

          return matches;
        }
      );

      let updatedPassport = patchObj(targetPassport, `errors.${field}`, null);

      const errors = collectObjValues(updatedPassport.errors);

      const passportClear = errors.reduce(
        (prev, error) => prev && (error === null),
        true
      );

      if (passportClear) {
        updatedPassport = patchObj(targetPassport, 'type_error', null);
      }

      if (Number.isInteger(targetIndex) && updatedPassport) {
        state.parsingResult[targetIndex] = updatedPassport;
      }
    }
  }
});

export const { reducer } = slice;

export const checkPatchedPassport = (
  passportIndex: number
): AppThunk => async (dispatch) => {
  const passportData = store
    .getState()
    .passportsUpload
    .parsingResult
    .find(
      (passport) => passport.index === passportIndex
    );

  const {
    data: patchedPassport
  } = await axios.post(
    urls.checkPatchedPassport,
    passportData
  );

  dispatch(
    slice
      .actions
      .checkPatchedPassport({
        passportIndex,
        patchedPassport
      })
  );
};

export const {
  setParsingResult,
  setUploadModalOpened,
  patchParsedPassport,
  removePassportError
} = slice.actions;

export default slice;
